import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';

export interface EventProgress {
    /** 0–1 through the event's lifetime. */
    progressRatio: number;
    ended: boolean;
    /** The selected window as a fraction of the event, for the inset bar. */
    selStart: number;
    selEnd: number;
}

/**
 * The hairline above the chart header. Shows how far through the event we are,
 * and where the selected window sits inside it — so a zoomed-in view still says
 * which part of the whole it is looking at.
 */
export function EventProgressBar({
    progress,
    timeRange,
}: {
    progress: EventProgress;
    timeRange: [Date, Date] | null;
}) {
    return (
        <Box
            sx={{
                height: 3,
                position: 'relative',
                bgcolor: t => alpha(t.palette.primary.main, 0.06),
            }}
        >
            {/* Elapsed. Dimmed once a window is selected, so the selection reads. */}
            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${progress.progressRatio * 100}%`,
                    bgcolor: 'primary.main',
                    opacity: progress.ended ? 0.1 : timeRange ? 0.3 : 0.7,
                    transition: 'width 0.3s ease, opacity 0.15s ease',
                }}
            />
            {!progress.ended && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: `${progress.progressRatio * 100}%`,
                        top: 0,
                        height: '100%',
                        width: `${(1 - progress.progressRatio) * 100}%`,
                        bgcolor: t => alpha(t.palette.primary.main, 0.1),
                    }}
                />
            )}
            {timeRange && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${progress.selStart * 100}%`,
                            top: 0,
                            height: '100%',
                            width: `${(progress.selEnd - progress.selStart) * 100}%`,
                            bgcolor: 'primary.main',
                            opacity: 0.7,
                            transition: 'left 0.15s ease, width 0.15s ease',
                        }}
                    />
                    {[progress.selStart, progress.selEnd].map((pos, i) => (
                        <Box
                            key={i}
                            sx={{
                                position: 'absolute',
                                left: `${pos * 100}%`,
                                top: -2,
                                width: 3,
                                height: 7,
                                bgcolor: '#fff',
                                borderRadius: '1px',
                                boxShadow: '0 0 2px rgba(0,0,0,0.4)',
                                transition: 'left 0.15s ease',
                            }}
                        />
                    ))}
                </>
            )}
            {/* "You are here" caret. */}
            {!progress.ended && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: `${progress.progressRatio * 100}%`,
                        top: 4,
                        transform: 'translateX(-4px)',
                        width: 0,
                        height: 0,
                        borderLeft: '4px solid transparent',
                        borderRight: '4px solid transparent',
                        borderBottom: '5px solid',
                        borderBottomColor: 'primary.main',
                        transition: 'left 0.3s ease',
                    }}
                />
            )}
        </Box>
    );
}

/** Elapsed and selected fractions, or null when the event has no valid span. */
export function computeEventProgress(
    eventStartDate: Date | null | undefined,
    eventEndDate: Date | null | undefined,
    timeRange: [Date, Date] | null
): EventProgress | null {
    if (!eventStartDate || !eventEndDate) return null;
    const start = eventStartDate.getTime();
    const end = eventEndDate.getTime();
    const total = end - start;
    if (total <= 0) return null;
    const now = Date.now();
    const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
    const progressRatio = clamp01((now - start) / total);
    return {
        progressRatio,
        ended: now >= end,
        selStart: timeRange ? clamp01((timeRange[0].getTime() - start) / total) : 0,
        // With no selection the "window" is everything up to now.
        selEnd: timeRange
            ? clamp01((timeRange[1].getTime() - start) / total)
            : progressRatio,
    };
}
